import {
  startTransition,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import { Lock, Info } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAllQuizQuestions } from "@/data/quiz";
import CardPhoto from "../../quiz/stepper/CardPhoto";
import ScreenHeading from "./ScreenHeading";
import { FOOTNOTE_GAP, HEADING_GAP, LIST_GAP } from "../layout";
import OptionRow from "./OptionRow";
import type { ChoiceScreenDef, ScreenComponentProps } from "../types";

/**
 * Every option-picking screen in the flow, in three densities.
 *
 * The important part is where the content comes from: when the def carries a
 * `questionId`, the headline and the options are read straight out of
 * `data/quiz.ts` — the same translated question the legacy quiz asks. The
 * design's mocked wording is deliberately not reproduced; only layout,
 * ordering, and the supporting copy are design-owned. Inline `options` exist
 * for the two narrative questions that have no quiz counterpart.
 */

/** Shared empty list, so "no options yet" is the same array every render. */
const NO_OPTIONS: NonNullable<ChoiceScreenDef["options"]> = [];

const CARD_COLORS = [
  { primary: "#EF6225", glow: "rgba(239, 98, 37, 0.4)" },
  { primary: "#B868C0", glow: "rgba(184, 104, 192, 0.4)" },
  { primary: "#36A2AD", glow: "rgba(54, 162, 173, 0.4)" },
];

export default function ChoiceScreen({
  def,
  rt,
}: ScreenComponentProps<ChoiceScreenDef>) {
  const { t } = useT();
  const allQuestions = useAllQuizQuestions();

  const question = useMemo(
    () => (def.questionId ? allQuestions.find((q) => q.id === def.questionId) : undefined),
    [allQuestions, def.questionId],
  );

  const options = def.options ?? question?.options ?? NO_OPTIONS;
  const title = def.titleKey ? t(def.titleKey) : (question?.question ?? "");
  const maxSelections = def.maxSelections ?? question?.maxSelections;

  const raw = rt.answers[def.field];
  // A single-select field can still hold an array — either because the screen
  // stores one (`storeAsArray`), or because a draft was written when the same
  // screen was multi-select. Read the first entry either way.
  const answered: string[] = useMemo(
    () =>
      def.multi
        ? Array.isArray(raw)
          ? raw
          : []
        : Array.isArray(raw)
          ? raw.slice(0, 1)
          : raw
            ? [raw]
            : [],
    [raw, def.multi],
  );

  /**
   * What the rows draw from, held here rather than read back out of the flow.
   *
   * The answer itself still lives at the top — it has to, the dock reads it,
   * the autosave sends it, a reload resumes from it. But routing the *paint*
   * through there meant a tap could not show its own checkmark until the page,
   * the flow, the shell and the screen had all re-rendered and everything
   * watching them had had its turn. The tap now moves this, which is one row.
   *
   * The answer follows immediately after, in a transition, so React finishes
   * the checkmark before it starts on the rest and can abandon that work if
   * another tap lands meanwhile.
   *
   * Seeded once, at mount, and deliberately never re-synced from the answer.
   * It doesn't need to be: the shell keys the screen on the step, so arriving
   * at a question — forwards, backwards, or resuming a draft after a reload —
   * mounts a fresh one that reads the answer as it stands. And it must not be:
   * a transition means React renders this screen with the new selection while
   * the answer above is still the old one, and an effect that read that gap as
   * somebody else changing the answer put the selection straight back. Every
   * first tap on a question undid itself.
   */
  const [localSelected, setLocalSelected] = useState<string[]>(answered);
  const localRef = useRef(localSelected);
  localRef.current = localSelected;

  // The runtime is read through a ref so the handler itself can stay stable.
  // It changes on every tap — `rt` carries `answers` — and a handler that
  // changed with it was a new `onClick` for every row on every tap, which is
  // what stopped the rows memoising (see OptionRow). Read at call time, it is
  // just as current.
  const rtRef = useRef(rt);
  rtRef.current = rt;

  const handleSelect = useCallback(
    (value: string) => {
      const runtime = rtRef.current;
      // The ref is written here as well as on render, so two taps landing in
      // the same tick build on each other instead of both reading the state
      // from before the first one.
      const apply = (next: string[], answer: unknown) => {
        localRef.current = next;
        setLocalSelected(next);
        startTransition(() => runtime.setAnswer(def.field, answer));
      };
      if (!def.multi) {
        apply([value], def.storeAsArray ? [value] : value);
        if (def.autoAdvance !== false) setTimeout(runtime.next, 260);
        return;
      }
      const current = localRef.current;
      if (current.includes(value)) {
        const next = current.filter((v) => v !== value);
        apply(next, next);
        return;
      }
      // At the cap, the newest pick displaces the oldest rather than silently
      // doing nothing — a dead tap reads as a broken screen.
      const next = maxSelections
        ? [...current, value].slice(-maxSelections)
        : [...current, value];
      apply(next, next);
    },
    [def, maxSelections],
  );

  const selected = localSelected;

  /** One stable click handler per option, so a row's props only change when
   *  that row's own selection does. */
  const handlers = useMemo(() => {
    const map = new Map<string, () => void>();
    for (const option of options) {
      map.set(option.value, () => handleSelect(option.value));
    }
    return map;
  }, [options, handleSelect]);

  const subtitle = def.subtitleKey ? t(def.subtitleKey) : undefined;
  const counter =
    def.multi && maxSelections
      ? t("onboarding.selectionCounter", {
          count: selected.length,
          max: maxSelections,
        })
      : undefined;

  /** `blurbKey` is a pattern — swap `{value}` for the option being rendered. */
  const blurbFor = (value: string) =>
    def.blurbKey ? t(def.blurbKey.replace("{value}", value)) : undefined;

  const FootnoteIcon =
    def.footnoteIcon === "info" ? Info : def.footnoteIcon === "lock" ? Lock : null;

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading
        kicker={def.kickerKey ? t(def.kickerKey) : undefined}
        title={title}
        subtitle={
          subtitle && counter
            ? `${subtitle} ${counter}`
            : (subtitle ?? counter)
        }
      />

      <div
        className={`${HEADING_GAP} flex flex-col ${LIST_GAP}`}
      >
        {def.layout === "cards"
          ? options.map((option, i) => {
              const color = CARD_COLORS[i % CARD_COLORS.length];
              return (
                <CardPhoto
                  key={option.value}
                  photoSrc={def.photoFor?.(option.value, rt.audience) ?? ""}
                  primary={color.primary}
                  glow={color.glow}
                  isSelected={selected.includes(option.value)}
                  delay={0.1 + i * 0.05}
                  onClick={handlers.get(option.value)}
                >
                  <div className="font-Poppins text-[20px] leading-tight text-neutral-900 dark:text-neutral-50 font-semibold">
                    {option.label}
                  </div>
                  {def.blurbKey && (
                    <div className="text-[12px] leading-snug text-neutral-500 dark:text-neutral-400 mt-1.5">
                      {blurbFor(option.value)}
                    </div>
                  )}
                </CardPhoto>
              );
            })
          : options.map((option, i) => (
              <OptionRow
                key={option.value}
                index={i}
                label={option.label}
                emoji={def.layout === "rich" ? undefined : option.emoji}
                blurb={blurbFor(option.value)}
                photoSrc={def.photoFor?.(option.value, rt.audience)}
                density={def.layout === "rich" ? "rich" : "list"}
                selected={selected.includes(option.value)}
                onClick={handlers.get(option.value)!}
              />
            ))}
      </div>

      {/* Only the icon'd privacy notes render inline, directly under the
          options they explain. The un-iconed reassurance line ("we build
          tables around this") belongs to the shared dock — those screens
          advance on tap, so it sits in the note slot where the button would
          be, and rendering it here as well would duplicate it. */}
      {def.footnoteKey && FootnoteIcon && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.3 }}
          className={`flex items-start gap-2.5 ${FOOTNOTE_GAP}`}
        >
          <FootnoteIcon className="w-3.5 h-3.5 mt-0.5 flex-none text-neutral-400" />
          <p className="text-[12px] leading-snug text-neutral-500 dark:text-neutral-400">
            {t(def.footnoteKey)}
          </p>
        </motion.div>
      )}
    </div>
  );
}
